/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTime from '@mui/icons-material/AccessTime';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import Shuffle from '@mui/icons-material/Shuffle';
import Autorenew from '@mui/icons-material/Autorenew';
import NavigationLayout from '../../components/NavigationLayout';
import DenyChartModal from '../../components/chartAbstraction/DenyChartModal';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Chart Abstraction — admin monitoring. Live view of chart processing + QA,
 * backed by /chart-abstraction/admin/overview (polled). Complete / Deny call
 * the shared QA endpoints. Auto-assign-to-QA happens server-side (lightest
 * queue) when a rep completes a chart. Team productivity & range reporting live
 * on the separate Team Productivity page.
 */

const pad = (n) => String(n).padStart(2, '0');
const fmt = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
const initials = (name) => (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
const relTime = (ms) => {
  if (!ms) return '';
  const m = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const card = { background: '#fff', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 12, boxShadow: '0 1px 2px rgba(14,30,64,0.04)' };
const cardHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 8px' };
const cardTitle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const countPill = { fontSize: 12, fontWeight: 600, color: 'var(--dark-3, #546077)', background: 'var(--dark-7, #ECECF0)', borderRadius: 20, padding: '1px 9px' };
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', borderTop: '1px solid var(--dark-7, #ECECF0)' };
const idS = { fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const subS = { fontSize: 12.5, color: 'var(--dark-3, #546077)', marginTop: 2 };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 600, borderRadius: 20, padding: '3px 11px', background: bg, color: fg, whiteSpace: 'nowrap' });
const avatar = (name) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{initials(name)}</span>
);
const scoreColor = (s) => {
  if (s >= 90) return 'var(--green-1, #256D23)';
  if (s >= 70) return '#B3650F';
  return 'var(--red-2, #CC4525)';
};
const scoreBg = (s) => {
  if (s >= 90) return '#E7F6E6';
  if (s >= 70) return '#FDF1E3';
  return '#FCECE7';
};
const scorePill = (s) => pill(scoreBg(s), scoreColor(s));
const errorLine = (errors) => (errors || []).map((e) => `${e.category}${e.severity ? ` (${e.severity})` : ''}`).join(', ');

const KPI_DEFS = [
  { key: 'inProgress', label: 'In progress', icon: AccessTime, accent: '#213D69' },
  { key: 'pended', label: 'Pended', icon: PauseCircleOutline, accent: '#B3650F' },
  { key: 'awaiting', label: 'Awaiting QA', icon: AssignmentTurnedInOutlined, accent: '#2563eb' },
  { key: 'casesToday', label: 'Cases today', icon: CheckCircleOutline, accent: '#256D23' },
  { key: 'acceptanceRate', label: 'Acceptance rate', icon: VerifiedUserOutlined, accent: '#7c3aed', pct: true },
  { key: 'avgSeconds', label: 'Avg time / case', icon: ScheduleOutlined, accent: '#546077', time: true },
];

function ChartAbstractionAdmin() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nowTs, setNowTs] = useState(Date.now());
  const [denyTarget, setDenyTarget] = useState(null);
  const [acting, setActing] = useState(false);
  const [assignAnchor, setAssignAnchor] = useState(null);
  const [assignChart, setAssignChart] = useState(null);
  const baseAtRef = useRef(Date.now());

  const fetchOverview = async () => {
    if (!workspaceId) return;
    const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.adminOverview(workspaceId) });
    if (res.success) {
      setOverview(res.data);
      baseAtRef.current = Date.now();
    } else {
      displayNotification({ message: res.data?.error || 'Failed to load monitoring data', severity: 'error' });
    }
  };

  useEffect(() => {
    if (!workspaceId) return undefined;
    (async () => { setLoading(true); await fetchOverview(); setLoading(false); })();
    const poll = setInterval(fetchOverview, 15000);
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [workspaceId]);

  const handleComplete = async (item) => {
    if (acting) return;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaComplete(workspaceId, item.id) });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} signed off as QA complete`, severity: 'success' });
      fetchOverview();
    } else {
      displayNotification({ message: res.data?.error || 'Could not complete the chart', severity: 'error' });
    }
  };

  const confirmDeny = async (payload) => {
    if (!denyTarget || acting) return;
    const item = denyTarget;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaDeny(workspaceId, item.id), data: payload });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} denied (score ${res.data?.score ?? '—'}%) and sent back to ${item.repName}`, severity: 'info' });
      setDenyTarget(null);
      fetchOverview();
    } else {
      displayNotification({ message: res.data?.error || 'Could not deny the chart', severity: 'error' });
    }
  };

  const openAssign = (e, chart) => { setAssignAnchor(e.currentTarget); setAssignChart(chart); };
  const closeAssign = () => { setAssignAnchor(null); setAssignChart(null); };
  const reassign = async (assigneeId, name) => {
    if (!assignChart) return;
    const chart = assignChart;
    closeAssign();
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.assignee(workspaceId, chart.id), data: { assigneeId } });
    if (res.success) {
      displayNotification({ message: assigneeId ? `${chart.recordId} assigned to ${name}` : `${chart.recordId} unassigned`, severity: 'success' });
      fetchOverview();
    } else {
      displayNotification({ message: res.data?.error || 'Could not reassign the chart', severity: 'error' });
    }
  };

  const kpis = overview?.kpis || {};
  const kpiValue = (k) => {
    const v = kpis[k.key];
    if (k.pct) return v == null ? '—' : `${v}%`;
    if (k.time) return v == null ? '—' : fmt(v);
    return String(v == null ? 0 : v);
  };

  const working = overview?.currentlyWorking || [];
  const awaiting = overview?.awaiting || [];
  const denied = overview?.denied || [];
  const qaCompleted = overview?.qaCompleted || [];
  const workloads = overview?.workloads || [];
  const isAdmin = !!overview?.isAdmin;
  const topErrorTypes = overview?.topErrorTypes || [];
  const kpisRaw = overview?.kpis || {};
  const acceptedToday = kpisRaw.acceptedToday || 0;
  const deniedToday = kpisRaw.deniedToday || 0;
  const reviewedToday = acceptedToday + deniedToday;
  const acceptPct = kpisRaw.acceptanceRate;
  const liveOffset = Math.floor((nowTs - baseAtRef.current) / 1000);

  return (
    <NavigationLayout title="Chart Abstraction" subtitle="Monitor chart processing and QA across your team">
      {loading && !overview ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><CircularProgress size={30} /></div>
      ) : (
        <div style={{ marginTop: 24, fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            {KPI_DEFS.map((k) => {
              const Icon = k.icon;
              return (
                <div key={k.key} style={{ ...card, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: `${k.accent}1a`, color: k.accent }}><Icon style={{ fontSize: 17 }} /></span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark-3, #546077)' }}>{k.label}</span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--dark-1, #0E1E40)', lineHeight: 1 }}>{kpiValue(k)}</div>
                </div>
              );
            })}
          </div>

          {/* Auto-assign strip */}
          <div style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)' }}><Shuffle style={{ fontSize: 17 }} /></span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>Auto-assign to QA</div>
                <div style={{ fontSize: 12.5, color: 'var(--dark-3, #546077)' }}>Completed charts route automatically to the reviewer with the lightest queue.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark-3, #546077)', marginRight: 4 }}>Workload</span>
              {workloads.length === 0 ? <span style={{ fontSize: 12.5, color: 'var(--dark-4, #7D8698)' }}>No QA reviewers yet</span>
                : workloads.map((q) => <span key={q.memberId} style={pill('#EAF1FF', 'var(--primary, #213D69)')}>{`${q.name} · ${q.count}`}</span>)}
            </div>
          </div>

          {/* Currently working + Awaiting QA */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />Currently working</span>
                <span style={countPill}>{working.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {working.length === 0 ? <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>No charts in progress.</div>
                  : working.map((r, i) => (
                    <div key={r.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        {avatar(r.repName)}
                        <span><span style={{ display: 'block', ...idS, fontWeight: 600 }}>{r.repName}</span><span style={{ display: 'block', fontSize: 12, color: 'var(--dark-3, #546077)' }}>{r.recordId}</span></span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark-1, #0E1E40)', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.elapsedSeconds + liveOffset)}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><AssignmentTurnedInOutlined style={{ fontSize: 18, color: '#2563eb' }} />Awaiting QA</span>
                <span style={countPill}>{awaiting.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {awaiting.length === 0 ? <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>All caught up.</div>
                  : awaiting.map((c, i) => (
                    <div key={c.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={idS}>{c.recordId}</span>
                          {c.assigneeName && <span style={{ ...pill('#EAF1FF', 'var(--primary, #213D69)'), fontWeight: 500 }}>{c.assigneeName}</span>}
                        </div>
                        <div style={subS}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · completed ${relTime(c.completedAtMs)}`}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                        {isAdmin && (
                          <Button size="small" variant="text" disabled={acting} onClick={(e) => openAssign(e, c)} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', minWidth: 0 }}>{c.assigneeName ? 'Reassign' : 'Assign'}</Button>
                        )}
                        {c.canAct ? (
                          <>
                            <Button size="small" variant="contained" disabled={acting} onClick={() => handleComplete(c)} sx={{ textTransform: 'none', backgroundColor: 'var(--green-3, #53A551)', '&:hover': { backgroundColor: 'var(--green-2, #378A35)' } }}>Complete</Button>
                            <Button size="small" variant="outlined" disabled={acting} onClick={() => setDenyTarget(c)} sx={{ textTransform: 'none', color: 'var(--red-2, #CC4525)', borderColor: '#e6c3ba' }}>Deny</Button>
                          </>
                        ) : (
                          !isAdmin && <span style={{ ...pill('var(--dark-7, #ECECF0)', 'var(--dark-3, #546077)'), fontWeight: 500 }}>View only</span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Denied + QA completed */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><Autorenew style={{ fontSize: 18, color: 'var(--red-2, #CC4525)' }} />Denied by QA — review again</span>
                <span style={countPill}>{denied.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {denied.length === 0 ? <div style={{ padding: '10px 20px 16px', color: 'var(--dark-3, #546077)', fontSize: 14 }}>No charts sent back. Denied charts return to the abstraction rep&apos;s queue for rework.</div>
                  : denied.map((c, i) => (
                    <div key={c.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={idS}>{c.recordId}</div>
                        <div style={{ ...subS, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{`Back to ${c.repName} · ${errorLine(c.errors) || c.reason || 'errors logged'}`}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {c.score != null && <span style={scorePill(c.score)}>{`${c.score}%`}</span>}
                        <span style={pill('#FCECE7', 'var(--red-2, #CC4525)')}>Denied</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><CheckCircleOutline style={{ fontSize: 18, color: 'var(--green-1, #256D23)' }} />QA completed</span>
                <span style={countPill}>{qaCompleted.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {qaCompleted.length === 0 ? <div style={{ padding: '10px 20px 16px', color: 'var(--dark-3, #546077)', fontSize: 14 }}>No charts signed off yet today.</div>
                  : qaCompleted.map((c, i) => (
                    <div key={`${c.submissionId || c.id}-${i}`} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={idS}>{c.recordId}</div>
                        <div style={subS}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · Reviewed by ${c.reviewerName || '—'} · ${relTime(c.reviewedAtMs)}`}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {c.score != null && <span style={scorePill(c.score)}>{`${c.score}%`}</span>}
                        <span style={pill('#E7F6E6', 'var(--green-1, #256D23)')}>Accepted</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* QA outcomes + common error types */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340, padding: '16px 20px 20px' }}>
              <div style={{ ...cardTitle, marginBottom: 14 }}><VerifiedUserOutlined style={{ fontSize: 18, color: '#7c3aed' }} />QA outcomes today</div>
              {reviewedToday === 0 ? (
                <div style={{ color: 'var(--dark-4, #7D8698)', fontSize: 14, padding: '8px 0' }}>No charts reviewed yet today.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'var(--dark-7, #ECECF0)', marginBottom: 12 }}>
                    <div style={{ width: `${acceptPct == null ? 0 : acceptPct}%`, background: 'var(--green-3, #53A551)' }} />
                    <div style={{ flex: 1, background: 'var(--red-2, #CC4525)' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div><span style={{ fontSize: 22, fontWeight: 700, color: 'var(--green-1, #256D23)' }}>{acceptPct == null ? '—' : `${acceptPct}%`}</span><div style={subS}>{`Accepted · ${acceptedToday}`}</div></div>
                    <div><span style={{ fontSize: 22, fontWeight: 700, color: 'var(--red-2, #CC4525)' }}>{acceptPct == null ? '—' : `${100 - acceptPct}%`}</span><div style={subS}>{`Denied · ${deniedToday}`}</div></div>
                    <div><span style={{ fontSize: 22, fontWeight: 700, color: scoreColor(kpisRaw.avgScore == null ? 100 : kpisRaw.avgScore) }}>{kpisRaw.avgScore == null ? '—' : `${kpisRaw.avgScore}%`}</span><div style={subS}>Avg score</div></div>
                  </div>
                </>
              )}
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340, padding: '16px 20px 20px' }}>
              <div style={{ ...cardTitle, marginBottom: 14 }}><ReportProblemOutlined style={{ fontSize: 18, color: '#B3650F' }} />Common error types today</div>
              {topErrorTypes.length === 0 ? (
                <div style={{ color: 'var(--dark-4, #7D8698)', fontSize: 14, padding: '8px 0' }}>No errors logged yet today.</div>
              ) : (
                topErrorTypes.map((e) => {
                  const mx = topErrorTypes.reduce((m, x) => Math.max(m, x.count), 0) || 1;
                  return (
                    <div key={e.name} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--dark-2, #293855)', marginBottom: 4 }}><span>{e.name}</span><span style={{ fontWeight: 600 }}>{e.count}</span></div>
                      <div style={{ height: 8, borderRadius: 4, background: 'var(--dark-7, #ECECF0)' }}><div style={{ width: `${Math.round((e.count / mx) * 100)}%`, height: '100%', borderRadius: 4, background: '#B3650F' }} /></div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      )}

      <Menu anchorEl={assignAnchor} open={!!assignAnchor} onClose={closeAssign}>
        <div style={{ padding: '4px 16px 8px', fontSize: 12, fontWeight: 600, color: 'var(--dark-3, #546077)' }}>Assign to reviewer</div>
        {workloads.length === 0 && <MenuItem disabled>No QA reviewers</MenuItem>}
        {workloads.map((q) => (
          <MenuItem
            key={q.memberId}
            selected={assignChart && assignChart.assigneeName === q.name}
            onClick={() => reassign(q.memberId, q.name)}
          >
            {`${q.name} · ${q.count} in queue`}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => reassign(null, null)} sx={{ color: 'var(--dark-3, #546077)' }}>Unassign</MenuItem>
      </Menu>

      <DenyChartModal
        open={!!denyTarget}
        target={denyTarget}
        categories={overview?.errorCategories}
        severityPoints={overview?.severityPoints}
        acting={acting}
        onCancel={() => setDenyTarget(null)}
        onConfirm={confirmDeny}
      />
    </NavigationLayout>
  );
}

export default ChartAbstractionAdmin;
